/**
 * HTTP-клиент к API.
 *
 * Две особенности, продиктованные работой замерщика в поле:
 *  - access-токен живёт полчаса, и обновление происходит прозрачно, чтобы
 *    человек не встречал форму входа посреди заполнения замера;
 *  - обрыв связи отличается от ошибки сервера: первое означает «повторим
 *    позже из очереди», второе — «данные не примут и через час».
 */

const TOKEN_KEY = 'blagodar.auth';

export interface StoredAuth {
  accessToken: string;
  refreshToken: string;
  /**
   * Пароль этого человека известен посторонним.
   *
   * Приходит один раз при входе и хранится вместе с сессией: сменить его
   * может только сам человек (правило 52), а спросить сервер повторно
   * нельзя — пароля у нас больше нет.
   */
  weakPassword?: boolean;
  user: {
    id: string;
    phone: string;
    fullName: string;
    roles: string[];
  };
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly fieldErrors?: Array<{ field: string; message: string }>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Сколько ждать ответа, прежде чем считать, что связи нет.
 *
 * БЕЗ ЭТОГО ОФЛАЙН НЕ РАБОТАЕТ ВОВСЕ, и поймано это на объекте: замерщик
 * приехал в чужой офис, где планшет прицепился к сети без интернета.
 * `navigator.onLine` в таком случае отвечает «в сети» — он знает лишь про
 * сетевой интерфейс, а не про то, доходит ли что-нибудь до сервера. И
 * `fetch` не падает: он ЖДЁТ, пока не истечёт таймаут операционной
 * системы, а это десятки секунд, иногда минуты.
 *
 * Всё это время экран крутит загрузку. Данные лежат в локальной базе
 * рядом, но до ветки «нет связи» дело не доходит — запрос ещё не
 * завершился. Со стороны это выглядит так: «пока интернет не появился,
 * ничего не работало».
 *
 * Десять секунд — это долго для живой сети (обычный ответ укладывается в
 * доли секунды) и достаточно быстро, чтобы человек не решил, что программа
 * повисла. Загрузка фотографий сюда не относится: она идёт своим путём.
 */
const REQUEST_TIMEOUT_MS = 10_000;

/** Сети нет или сервер недоступен — работа продолжается оффлайн. */
export class NetworkError extends Error {
  constructor(message = 'Нет связи с сервером') {
    super(message);
    this.name = 'NetworkError';
  }
}

const LAST_USER_KEY = 'blagodar.lastUser';

export function loadAuth(): StoredAuth | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    return raw ? (JSON.parse(raw) as StoredAuth) : null;
  } catch {
    return null;
  }
}

export function saveAuth(auth: StoredAuth): void {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(auth));
}

export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Кто последним работал на этом планшете.
 *
 * Отдельно от токена и НЕ стирается при выходе. Иначе получалось так:
 * замерщик нажал «Выйти», токена не стало, следующий вошёл — и сравнить
 * его не с кем, а в локальной базе остались чужие наряды и черновики
 * замеров с телефонами и адресами клиентов.
 */
export function loadLastUserId(): string | null {
  try {
    return localStorage.getItem(LAST_USER_KEY);
  } catch {
    return null;
  }
}

export function saveLastUserId(userId: string): void {
  try {
    localStorage.setItem(LAST_USER_KEY, userId);
  } catch {
    // Приватный режим браузера — не повод ломать вход
  }
}

type AuthListener = (auth: StoredAuth | null) => void;
const listeners = new Set<AuthListener>();

export function onAuthChange(listener: AuthListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyAuthChange(auth: StoredAuth | null): void {
  listeners.forEach((l) => l(auth));
}

const API_URL_KEY = 'blagodar.apiUrl';

/** Приложение запущено как APK на планшете, а не открыто в браузере. */
export function isNativeApp(): boolean {
  const capacitor = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return capacitor?.isNativePlatform?.() === true;
}

/**
 * Адрес сервера.
 *
 * В браузере запросы идут на тот же хост (`/api`) — их проксирует nginx.
 * В приложении на планшете своего сервера нет, адрес нужен полный, и зашивать
 * его в сборку нельзя: домен поменяется — придётся пересобирать и переставлять
 * APK на каждом планшете. Поэтому адрес задаётся один раз в самом приложении
 * и хранится на устройстве.
 */
export function getApiBase(): string {
  const stored = localStorage.getItem(API_URL_KEY);
  if (stored) return stored.replace(/\/+$/, '');
  const fromBuild = import.meta.env.VITE_API_URL;
  if (fromBuild) return String(fromBuild).replace(/\/+$/, '');
  return '/api';
}

export function setApiBase(url: string): void {
  const trimmed = url.trim().replace(/\/+$/, '');
  if (trimmed) localStorage.setItem(API_URL_KEY, trimmed);
  else localStorage.removeItem(API_URL_KEY);
}

/** Адрес не настроен, а сам себя приложение обслужить не может. */
export function needsApiSetup(): boolean {
  return isNativeApp() && !localStorage.getItem(API_URL_KEY) && !import.meta.env.VITE_API_URL;
}

/** Проверка адреса до сохранения — чтобы не ловить «не работает» на объекте. */
export async function checkApiUrl(url: string): Promise<{ ok: boolean; error?: string }> {
  const base = url.trim().replace(/\/+$/, '');
  if (!/^https?:\/\//i.test(base)) {
    return { ok: false, error: 'Адрес должен начинаться с http:// или https://' };
  }
  // С тайм-аутом: без него экран «проверить адрес» висит до тайм-аута
  // операционной системы, и человек решает, что программа зависла
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(`${base}/health`, { method: 'GET', signal: controller.signal });
    if (!response.ok) return { ok: false, error: `Сервер ответил ошибкой ${response.status}` };
    const data = (await response.json()) as { status?: string; db?: string };
    if (data.status !== 'ok') {
      return { ok: false, error: `Сервер отвечает, но база недоступна (${data.db ?? '?'})` };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: 'Сервер не отвечает. Проверьте адрес и связь.' };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Параллельные запросы не должны запускать несколько обновлений токена:
 * все ждут одного.
 */
let refreshPromise: Promise<StoredAuth | null> | null = null;

async function refreshTokens(): Promise<StoredAuth | null> {
  const current = loadAuth();
  if (!current?.refreshToken) return null;

  if (!refreshPromise) {
    refreshPromise = (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        const response = await fetch(`${requestBase()}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: current.refreshToken }),
          signal: controller.signal,
        });
        if (!response.ok) {
          // Сессию рвём, ТОЛЬКО когда сервер прямо сказал «этот токен
          // недействителен». Раньше её сносила любая неудача, включая
          // 502 в те секунды, пока перезапускается API после
          // развёртывания, и 429 при всплеске запросов. Человека
          // выкидывало на экран входа — а если он в этот момент в
          // подвале без связи, войти обратно он уже не мог.
          if (response.status === 401 || response.status === 403) {
            clearAuth();
            notifyAuthChange(null);
          }
          return null;
        }
        const data = (await response.json()) as StoredAuth;
        saveAuth(data);
        notifyAuthChange(data);
        return data;
      } catch {
        // Обрыв связи или молчание сервера — старый токен оставляем,
        // попробуем позже
        return null;
      } finally {
        clearTimeout(timer);
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

/**
 * `fetch` со сроком ожидания — для запросов, идущих мимо `apiRequest`.
 *
 * Таких два: загрузка фотографий (там FormData, а не JSON) и опрос
 * версии. Оба ходили без срока и в сети без интернета висели до таймаута
 * операционной системы — то есть ровно так же, как всё остальное до
 * появления `REQUEST_TIMEOUT_MS`.
 */
/**
 * Короткая память о том, что сервер не отвечает.
 *
 * Один тайм-аут — десять секунд. Без этой памяти каждый следующий экран
 * отсчитывал бы их заново: перешёл на «Мои замеры» — десять секунд,
 * открыл наряд — ещё десять. Замерщик в квартире видит крутящуюся
 * загрузку на каждом шаге и делает единственный разумный вывод: не
 * работает.
 *
 * Помним НЕДОЛГО. Связь возвращается сама — вышел из подвала, поймал
 * сеть, — и длинная память заставила бы ждать её впустую. Любой удачный
 * ответ снимает отметку немедленно.
 *
 * Ручное «Обновить» и отправка очереди проходят мимо: человек нажал
 * кнопку сам, и отвечать ему «не буду даже пробовать» нельзя.
 */
const UNREACHABLE_MEMORY_MS = 15_000;
let unreachableUntil = 0;

function markUnreachable(): void {
  unreachableUntil = Date.now() + UNREACHABLE_MEMORY_MS;
}

function markReachable(): void {
  unreachableUntil = 0;
}

/** Сервер молчал только что — ждать снова десять секунд незачем. */
export function serverLooksUnreachable(): boolean {
  return Date.now() < unreachableUntil;
}

/** Забыть отметку — например, когда человек сам нажал «Обновить». */
export function forgetUnreachable(): void {
  markReachable();
}

export async function fetchWithTimeout(
  input: string,
  init: RequestInit = {},
  timeoutMs = REQUEST_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * ЗАПАСНОЙ ВХОД (12.10.2026, правило 238). Сеть провайдера закрывает порт
 * сервера на десять минут — ПОРТ, а не сервер: пока молчал 443, отвечали
 * 8443 и 80. Сервер слушает ещё и запасной порт по HTTP/1.1 (HTTP/2 ломает
 * офисная сеть, правило 227), и приложение, не дождавшись основного адреса,
 * само проверяет запасной: отвечает — десять минут ходит туда, потом снова
 * пробует основной. Руками переключать никто не станет: монтажник видит
 * только «нет связи».
 *
 * Запасной адрес называет сервер в /health (`backupApi`), а приложение
 * запоминает его, пока связь есть: в ту минуту, когда он нужен, спросить уже
 * не у кого. Помнится вместе с основным: сменили адрес сервера — старый
 * запасной не годится. Учебная версия запасного не называет и в боевую не
 * уйдёт.
 */
const BACKUP_KEY = 'blagodar.apiBackup';
/** Столько сеть держит порт закрытым — столько и ходим запасным */
const PRIMARY_DOWN_MS = 10 * 60_000;
/** Проба запасного короче обычного ожидания: человек уже ждал основной */
const BACKUP_PROBE_MS = 6_000;
let primaryDownUntil = 0;
let probing: Promise<boolean> | null = null;

/**
 * Запомнить запасной адрес из /health ОСНОВНОГО; пусто — забыть. Основной
 * только что ответил — значит, порт открыт, и запросы возвращаются на него
 */
export function rememberApiBackup(url: unknown): void {
  primaryDownUntil = 0;
  try {
    const backup = typeof url === 'string' ? url.trim().replace(/\/+$/, '') : '';
    if (!/^https:\/\//i.test(backup)) {
      localStorage.removeItem(BACKUP_KEY);
      return;
    }
    localStorage.setItem(BACKUP_KEY, JSON.stringify({ primary: getApiBase(), backup }));
  } catch {
    // Память недоступна — живём без запасного, как раньше
  }
}

function knownBackup(): string | null {
  try {
    const raw = localStorage.getItem(BACKUP_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { primary?: unknown; backup?: unknown };
    const primary = getApiBase();
    return v.primary === primary && typeof v.backup === 'string' && v.backup !== primary ? v.backup : null;
  } catch {
    return null;
  }
}

/** Куда идут запросы сейчас: основной адрес, а пока он молчит — запасной */
export function requestBase(): string {
  const backup = knownBackup();
  return backup && Date.now() < primaryDownUntil ? backup : getApiBase();
}

/** Ходим ли сейчас запасным входом — для подписи у значка связи */
export function onBackupEntry(): boolean {
  return requestBase() !== getApiBase();
}

/**
 * Основной адрес промолчал — отвечает ли запасной? Одна проба /health на
 * всех: запросы экрана падают пачкой, и каждый со своей пробой задвоил бы
 * ровно ту пачку соединений, за которую сеть и закрывает порт.
 */
async function switchToBackup(): Promise<boolean> {
  const backup = knownBackup();
  if (!backup) return false;
  if (Date.now() < primaryDownUntil) return true;
  if (!probing) {
    probing = (async () => {
      try {
        const response = await fetchWithTimeout(`${backup}/health`, {}, BACKUP_PROBE_MS);
        if (!response.ok) return false;
        primaryDownUntil = Date.now() + PRIMARY_DOWN_MS;
        return true;
      } catch {
        return false;
      } finally {
        probing = null;
      }
    })();
  }
  return probing;
}

/** Запасной тоже промолчал — следующий запрос снова пробует основной */
function backupFailed(): void {
  primaryDownUntil = 0;
}

/**
 * Запрос с токеном, который САМ обновляет протухший вход.
 *
 * Для того, что идёт мимо `apiRequest`: снимков объекта — они уходят
 * формой multipart и приходят потоком, а не JSON.
 *
 * Токен живёт полчаса, замер идёт дольше: замерщик снимал комнату, жал
 * «Снять» и получал «не удалось загрузить фото», а повтор не помогал —
 * протухший токен был захвачен в замыкании. На остальных экранах он
 * обновлялся сам, и человек не понимал, почему фотографии «сломались».
 *
 * Токен берётся ВНУТРИ, на каждую попытку: снаружи его легко захватить
 * один раз при отрисовке и не заметить этого.
 */
export async function authorizedFetch(
  path: string,
  init: RequestInit = {},
  timeoutMs = REQUEST_TIMEOUT_MS,
): Promise<Response> {
  const send = () => {
    const token = loadAuth()?.accessToken;
    const headers = new Headers(init.headers ?? {});
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return fetchWithTimeout(`${requestBase()}${path}`, { ...init, headers }, timeoutMs);
  };

  const response = await send();
  if (response.status !== 401) return response;

  // Тело формы уже прочитано первой попыткой — повторить можно только то,
  // что можно отправить заново. Поток (загрузка файла) не переигрывается,
  // и честнее вернуть 401, чем молча отправить пустоту
  const refreshed = await refreshTokens();
  if (!refreshed) return response;
  if (init.body instanceof ReadableStream) return response;
  return send();
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Не пытаться обновить токен (используется самим обновлением) */
  skipRefresh?: boolean;
  /** Своё время ожидания. По умолчанию — REQUEST_TIMEOUT_MS */
  timeoutMs?: number;
  /** Пробовать, даже если сервер только что молчал (нажатие человека) */
  ignoreUnreachable?: boolean;
  /** Служебное: это уже повтор после мгновенного сбоя — второй раз не повторять */
  retried?: boolean;
}

/**
 * Пауза перед повтором после мгновенного сбоя. Короткая: человек её не
 * замечает, а сети хватает, чтобы переподключиться после обрыва.
 */
const RETRY_AFTER_MS = 400;


export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, skipRefresh } = options;

  // Основной адрес, а пока он молчит — запасной вход (правило 238)
  const base = requestBase();
  const url = new URL(`${base}${path}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
  }

  const auth = loadAuth();
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth?.accessToken) headers.Authorization = `Bearer ${auth.accessToken}`;

  // Сервер молчал только что — не заставляем ждать ещё десять секунд.
  // Запросы, начатые человеком, эту память обходят: см. forgetUnreachable
  if (!options.ignoreUnreachable && serverLooksUnreachable()) {
    throw new NetworkError('Нет связи с сервером');
  }

  // Свой сигнал поверх пользовательского: `AbortSignal.any` есть не во
  // всех WebView, поэтому связываем руками
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), options.timeoutMs ?? REQUEST_TIMEOUT_MS);
  const onOuterAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', onOuterAbort, { once: true });
  }

  let response!: Response;
  let failure: 'timeout' | 'network' | null = null;
  try {
    response = await fetch(url.toString(), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (error) {
    // Отмену, пришедшую снаружи (экран закрыли), пробрасываем как есть:
    // react-query отличает её от сбоя и не показывает ошибку
    if (signal?.aborted) throw error;
    failure = controller.signal.aborted ? 'timeout' : 'network';
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onOuterAbort);
  }

  /*
   * ОДНА ОСЕЧКА — НЕ «НЕТ СВЯЗИ».
   *
   * Владелец (30.09.2026): «надо избавиться от этой пропажи связи, МОЛЮ».
   * Пропажу раздувал сам клиент. После ЛЮБОГО сбоя он пятнадцать секунд не
   * пробовал ничего и отвечал «нет связи с сервером», а сетевые ошибки
   * react-query не повторяет вовсе. Один оборванный запрос — и весь экран
   * расходки у отдела документов стоял пустым; утром это делал HTTP/2 в
   * офисной сети (правило 227), но так же выходит от любого мигания Wi-Fi.
   *
   * ПАМЯТЬ О МОЛЧАНИИ — ТОЛЬКО О МОЛЧАНИИ. Она заводилась ради тайм-аута
   * (правило 85): чтобы каждый следующий экран не ждал заново по десять
   * секунд. Мгновенный сбой ждать не заставляет — помнить его значило только
   * мешать: вместо «попробую ещё» — «не буду и пробовать».
   *
   * МГНОВЕННЫЙ СБОЙ ПОВТОРЯЕТСЯ ОДИН РАЗ, ТИХО, И ТОЛЬКО У ЧТЕНИЯ. Повтор
   * GET безопасен — он ничего не меняет на сервере. Запись не повторяется:
   * сервер мог её принять, а ответ потеряться, и второй платёж или вторая
   * выдача хуже честного «нет связи» (правила 114 и 127).
   */
  if (failure) {
    if (failure === 'network' && method === 'GET' && !options.retried) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_AFTER_MS));
      // Экран закрыли за время паузы — это отмена, а не сбой
      if (signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
      return apiRequest<T>(path, { ...options, retried: true });
    }
    /*
     * ОСНОВНОЙ АДРЕС МОЛЧИТ — ЗАПАСНОЙ ВХОД (12.10.2026, правило 238). Сеть
     * закрывает порт, а не сервер: отвечает запасной — чтение идёт туда сразу,
     * запись нет (сервер мог её принять, правило 233), но следующая уйдёт уже
     * туда: очередь планшета и «Повторить» отправят её сами.
     */
    const onPrimary = base === getApiBase();
    if (onPrimary && (await switchToBackup())) {
      if (signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
      if (method === 'GET') return apiRequest<T>(path, { ...options, retried: false });
      throw new NetworkError('Основной адрес не ответил — связь переведена на запасной вход, повторите');
    }
    if (!onPrimary) backupFailed();
    if (failure === 'timeout') {
      markUnreachable();
      throw new NetworkError('Сервер не ответил');
    }
    throw new NetworkError('Нет связи с сервером');
  }

  // Ответ пришёл — сервер на месте, что бы он ни ответил
  markReachable();

  if (response.status === 401 && !skipRefresh) {
    const refreshed = await refreshTokens();
    if (refreshed) {
      return apiRequest<T>(path, { ...options, skipRefresh: true });
    }
    throw new ApiError('Требуется вход в систему', 401);
  }

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  const payload = contentType.includes('application/json')
    ? ((await response.json()) as unknown)
    : await response.text();

  if (!response.ok) {
    const data = payload as {
      message?: string | string[];
      errors?: Array<{ field: string; message: string }>;
    };
    const message = Array.isArray(data?.message)
      ? data.message.join(', ')
      : (data?.message ?? 'Ошибка запроса');
    throw new ApiError(message, response.status, data?.errors);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) => apiRequest<T>(path, { query }),
  post: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
};

export { notifyAuthChange };
